
import React from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, ArrowRight, Target, Clock, Zap } from "lucide-react";
import { Link } from 'react-router-dom';

const Onboarding = () => {
  const location = useLocation();
  const { painPoint, urgency, personalizationComplete, formData } = location.state || {};

  // Redirect if not from personalization flow
  if (!personalizationComplete || !painPoint) {
    return <Navigate to="/app" replace />;
  }

  const getPainTitle = (painPoint: string) => {
    const titles = {
      'racing_thoughts': 'Racing Thoughts',
      'sleep_issues': 'Sleep Problems', 
      'work_stress': 'Work Overwhelm',
      'general_anxiety': 'General Anxiety'
    };
    return titles[painPoint as keyof typeof titles] || 'Your Challenge';
  };

  const getRecommendations = (painPoint: string, urgency: string) => {
    const baseRecommendations = {
      'racing_thoughts': {
        category: 'anxiety',
        sessions: ['Focus Reset', 'Mind Clearing', 'Thought Stopping'],
        duration: urgency === 'immediate' ? '5-10 minutes' : '15-20 minutes'
      },
      'sleep_issues': {
        category: 'sleep',
        sessions: ['Deep Sleep', 'Night Calm', 'Sleep Preparation'],
        duration: '20-30 minutes'
      },
      'work_stress': {
        category: 'anxiety',
        sessions: ['Stress Relief', 'Work Break Reset', 'Tension Release'],
        duration: urgency === 'immediate' ? '5 minutes' : '15 minutes'
      },
      'general_anxiety': {
        category: 'anxiety',
        sessions: ['Anxiety Relief', 'Calm Confidence', 'Inner Peace'],
        duration: '10-15 minutes'
      }
    };

    return baseRecommendations[painPoint as keyof typeof baseRecommendations] || baseRecommendations['general_anxiety'];
  };

  const recommendations = getRecommendations(painPoint, urgency);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Success Message */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            Your Personalized Plan is Ready!
          </h1>
          <p className="text-xl text-gray-600">
            Based on your {getPainTitle(painPoint).toLowerCase()} and preferences, here's what we've prepared for you
          </p>
        </div>

        {/* Personalized Plan with actual data */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardContent className="p-6 text-center">
              <Target className="h-8 w-8 text-purple-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Your Focus</h3>
              <p className="text-gray-600">{getPainTitle(painPoint)}</p>
              <p className="text-sm text-gray-500 mt-2">Success metric: {formData?.successMetric}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <Clock className="h-8 w-8 text-blue-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Your Schedule</h3>
              <p className="text-gray-600">{formData?.sessionLength} minute sessions</p>
              <p className="text-sm text-gray-500 mt-2">{formData?.preferredTiming}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6 text-center">
              <Zap className="h-8 w-8 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Experience Level</h3>
              <p className="text-gray-600 capitalize">{formData?.meditationExperience?.replace('_', ' ')}</p>
              <p className="text-sm text-gray-500 mt-2">
                {urgency === 'immediate' ? 'As needed' : 'Daily practice'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Show selected triggers */}
        {formData?.stressTriggers && formData.stressTriggers.length > 0 && (
          <Card className="mb-8">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                Your Stress Triggers
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {formData.stressTriggers.map((trigger, index) => (
                  <div key={index} className="bg-red-50 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {trigger}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recommended Sessions */}
        <Card className="mb-8">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">
              Your Recommended Sessions
            </h2>
            <div className="space-y-4">
              {recommendations.sessions.map((session, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-800">{session}</h3>
                    <p className="text-sm text-gray-600">
                      Optimized for {getPainTitle(painPoint).toLowerCase()} • {formData?.sessionLength} minutes
                    </p>
                  </div>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-gray-600 mb-8">
            Complete your profile to unlock your first personalized session
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/profile">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700">
                Complete Profile & Start <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
            <Link to={`/category/${recommendations.category}`}>
              <Button size="lg" variant="outline">
                Browse Sample Sessions
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
